import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Loader2, Mail, Send, Save, AlertCircle, CheckCircle2, Inbox, Building, Phone, Globe,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "./AdminContactRequestsPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEMPLATES = [
  {
    label: "Thank you for contacting PsyTech",
    subject: "Thank you for contacting PsyTech",
    body:
      "Hi {{name}},\n\nThank you for reaching out to PsyTech. We received your message and a member of our team will get back to you within 1–2 business days.\n\nIf your inquiry is urgent, please feel free to reply to this email directly.\n\nWarm regards,\nThe PsyTech Team",
  },
  {
    label: "Demo request received",
    subject: "Your PsyTech demo request",
    body:
      "Hi {{name}},\n\nThank you for your interest in a PsyTech demo. We'd love to walk you through the platform. Could you share a few time slots that work for you in the next two weeks?\n\nBest,\nThe PsyTech Team",
  },
  {
    label: "Request for more information",
    subject: "Quick follow-up on your PsyTech inquiry",
    body:
      "Hi {{name}},\n\nThanks again for reaching out. To help us tailor our response, could you share a bit more about your organization, current assessment workflow, and the outcomes you'd like PsyTech to support?\n\nLooking forward to hearing from you.\nThe PsyTech Team",
  },
  {
    label: "Meeting scheduling follow-up",
    subject: "Scheduling our PsyTech demo call",
    body:
      "Hi {{name}},\n\nFollowing up on our conversation — please use this link to pick a time that works for you, and we'll send a calendar invite shortly.\n\n[Insert scheduling link]\n\nTalk soon,\nThe PsyTech Team",
  },
];

const STATUSES = ["new", "replied", "contacted", "closed", "spam"];

export default function AdminContactDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);

  const [replySubject, setReplySubject] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [sending, setSending] = useState(false);
  const [replyMsg, setReplyMsg] = useState("");
  const [replyErr, setReplyErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/admin/contact-submissions/${id}`),
        axios.get(`${API}/admin/contact-submissions/${id}/emails`),
      ]);
      setItem(a.data);
      setNotes(a.data.admin_notes || "");
      setEmails(b.data.logs || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load submission");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (status) => {
    try {
      const { data } = await axios.patch(`${API}/admin/contact-submissions/${id}`, { status });
      setItem(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to update status");
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    setNotesSaved(false);
    try {
      const { data } = await axios.patch(`${API}/admin/contact-submissions/${id}`, {
        admin_notes: notes,
      });
      setItem(data);
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 2000);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to save notes");
    } finally {
      setSavingNotes(false);
    }
  };

  const applyTemplate = (tpl) => {
    const name = item?.name?.split(" ")[0] || "there";
    setReplySubject(tpl.subject);
    setReplyBody(tpl.body.replace(/{{name}}/g, name));
  };

  const sendReply = async () => {
    setSending(true);
    setReplyErr("");
    setReplyMsg("");
    try {
      await axios.post(`${API}/admin/contact-submissions/${id}/reply`, {
        subject: replySubject,
        body: replyBody,
      });
      setReplyMsg("Reply sent successfully.");
      setReplySubject("");
      setReplyBody("");
      load();
    } catch (e) {
      setReplyErr(e?.response?.data?.detail || "Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="space-y-3">
        <Link to="/admin/contact-requests" className="text-sm text-slate-500 hover:text-cyan-700 flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back to list
        </Link>
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">
          {error || "Not found"}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="admin-contact-detail">
      <div>
        <Link to="/admin/contact-requests" className="text-sm text-slate-500 hover:text-cyan-700 flex items-center gap-1 mb-2 w-fit">
          <ArrowLeft className="w-4 h-4" /> Back to list
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{item.name}</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {new Date(item.created_at).toLocaleString()}
            </p>
          </div>
          <div className="flex gap-1">
            {STATUSES.map((s) => (
              <Button
                key={s}
                size="sm"
                variant={item.status === s ? "default" : "outline"}
                onClick={() => updateStatus(s)}
                data-testid={`contact-set-status-${s}`}
                className={item.status === s ? "bg-cyan-700 hover:bg-cyan-800 text-white" : ""}
              >
                {s}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left: details */}
        <div className="space-y-4 lg:col-span-2">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h2 className="text-sm font-semibold text-slate-700 mb-3">Message</h2>
            <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
              {item.message}
            </p>
          </div>

          {/* Reply form */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Mail className="w-4 h-4 text-cyan-700" /> Reply by email
              </h2>
              <span className="text-[11px] text-slate-500">to: {item.email}</span>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {TEMPLATES.map((t) => (
                <button
                  key={t.label}
                  type="button"
                  onClick={() => applyTemplate(t)}
                  data-testid={`reply-template-${t.label.replace(/\s+/g, "-").toLowerCase()}`}
                  className="text-[11px] px-2 py-1 rounded-md border border-slate-200 hover:border-cyan-500 hover:text-cyan-700 transition-colors"
                >
                  {t.label}
                </button>
              ))}
            </div>
            <input
              value={replySubject}
              onChange={(e) => setReplySubject(e.target.value)}
              placeholder="Subject"
              data-testid="reply-subject-input"
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 mb-2 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none"
            />
            <textarea
              value={replyBody}
              onChange={(e) => setReplyBody(e.target.value)}
              placeholder="Write your reply…"
              rows={8}
              data-testid="reply-body-input"
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none resize-y"
            />
            {replyErr && (
              <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-start gap-2">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {replyErr}
              </div>
            )}
            {replyMsg && (
              <div className="mt-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {replyMsg}
              </div>
            )}
            <div className="mt-3 flex justify-end">
              <Button
                onClick={sendReply}
                disabled={sending || !replySubject.trim() || !replyBody.trim()}
                data-testid="reply-send-btn"
                className="bg-cyan-700 hover:bg-cyan-800 text-white"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Send reply
              </Button>
            </div>
          </div>

          {/* Email log */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h2 className="text-sm font-semibold text-slate-700 mb-3">Email history</h2>
            {emails.length === 0 ? (
              <div className="text-center py-6 text-slate-400 text-sm">
                <Inbox className="w-6 h-6 mx-auto mb-1 text-slate-300" />
                No emails sent yet for this submission.
              </div>
            ) : (
              <ul className="space-y-3">
                {emails.map((e) => (
                  <li key={e.id} className="border border-slate-100 rounded-lg p-3">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium text-slate-700">{e.subject}</span>
                      <span className={`px-2 py-0.5 rounded-md border ${
                        e.status === "sent" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : e.status === "failed" ? "bg-red-50 text-red-700 border-red-200"
                        : "bg-slate-100 text-slate-700 border-slate-200"
                      }`}>{e.status}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {new Date(e.created_at).toLocaleString()} · to {e.to_email}
                    </div>
                    {e.error_message && (
                      <div className="mt-1 text-[11px] text-red-600">{e.error_message}</div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Right: meta + notes */}
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h2 className="text-sm font-semibold text-slate-700 mb-3">Contact details</h2>
            <dl className="space-y-2 text-sm">
              <Field icon={Mail} label="Email" value={item.email} mono />
              <Field icon={Phone} label="Phone" value={item.phone || "—"} />
              <Field icon={Building} label="Company" value={item.company || "—"} />
              <Field icon={Building} label="Type" value={item.company_type || "—"} />
              <Field icon={Globe} label="Source page" value={item.source_page || "—"} />
              <div>
                <dt className="text-xs text-slate-500">Status</dt>
                <dd className="mt-1"><StatusBadge status={item.status || "new"} /></dd>
              </div>
            </dl>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h2 className="text-sm font-semibold text-slate-700 mb-3">Admin notes</h2>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={5}
              placeholder="Internal notes — not visible to the contact."
              data-testid="contact-notes-input"
              className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none resize-y"
            />
            <div className="mt-2 flex items-center justify-between">
              {notesSaved && (
                <span className="text-xs text-emerald-700 flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Saved
                </span>
              )}
              <div className="ml-auto">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={saveNotes}
                  disabled={savingNotes}
                  data-testid="contact-notes-save"
                >
                  {savingNotes ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                  Save notes
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value, mono }) {
  return (
    <div>
      <dt className="text-xs text-slate-500 flex items-center gap-1.5">
        <Icon className="w-3 h-3" /> {label}
      </dt>
      <dd className={`mt-0.5 text-slate-800 ${mono ? "font-mono text-xs break-all" : ""}`}>
        {value}
      </dd>
    </div>
  );
}
